import { useEffect, useMemo, useRef, useState } from "react";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/roles";
import {
  createConversation,
  listConversations,
  subscribeToInboxPings,
  uploadMessageAttachments,
} from "@/lib/messaging";
import ConversationRow from "@/components/messaging/ConversationRow";
import ComposeDialog from "@/components/messaging/ComposeDialog";
import { DEMO_CLASSROOMS, DEMO_CHILDREN } from "@/lib/dailyReports";

// Demo-mode families (teachers + admin can message these). In live mode
// we'd query supabase; for Phase 2.2 we pick the demo fixtures so the
// compose flow works out-of-the-box in preview.
const DEMO_FAMILIES = [
  {
    id: "00000000-0000-0000-0000-000000000201",
    display_name: "Cohen Family",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    participantIds: ["demo-parent", "demo-teacher", "demo-admin"],
  },
  {
    id: "00000000-0000-0000-0000-000000000202",
    display_name: "Patel Family",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    participantIds: ["demo-teacher", "demo-admin"],
  },
  {
    id: "00000000-0000-0000-0000-000000000203",
    display_name: "Miller Family",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    participantIds: ["demo-teacher", "demo-admin"],
  },
  {
    id: "00000000-0000-0000-0000-000000000204",
    display_name: "Ndiaye Family",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    participantIds: ["demo-teacher", "demo-admin"],
  },
  {
    id: "00000000-0000-0000-0000-000000000205",
    display_name: "Brown Family",
    classroom_id: "00000000-0000-0000-0000-000000000102",
    participantIds: ["demo-admin"],
  },
];

export default function MessagingInbox({ basePath }) {
  const { profile, role } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const composeTriggerRef = useRef(null);

  const canCompose = role === ROLES.TEACHER || role === ROLES.ADMIN;

  const load = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const rows = await listConversations({ profileId: profile.id, role });
      setConversations(rows);
    } catch (err) {
      toast.error(err.message || "Could not load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // Realtime: any new message anywhere triggers a silent inbox re-fetch.
  // RLS filters to rows the caller can already see, so this is safe.
  useEffect(() => {
    if (!profile?.id) return;
    const unsub = subscribeToInboxPings(() => {
      load();
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // Helpers for turning ids into readable names (parameter passed to ConversationRow).
  const helpers = useMemo(
    () => ({
      classroomName: (id) =>
        DEMO_CLASSROOMS.find((c) => c.id === id)?.name,
      familyName: (id) =>
        DEMO_FAMILIES.find((f) => f.id === id)?.display_name,
    }),
    [],
  );

  // Restrict compose families/classrooms per role.
  const composeFamilies = useMemo(() => {
    if (role === ROLES.ADMIN) return DEMO_FAMILIES;
    // Teacher: families for children in classrooms they cover.
    const myClassroomIds = new Set(
      DEMO_CHILDREN.filter((c) =>
        // demo teacher covers only Sunflower in Phase 1/2
        c.classroom_id === "00000000-0000-0000-0000-000000000101",
      ).map((c) => c.family_id),
    );
    return DEMO_FAMILIES.filter((f) => myClassroomIds.has(f.id));
  }, [role]);

  const composeClassrooms = useMemo(() => {
    if (role === ROLES.ADMIN) {
      return DEMO_CLASSROOMS.map((c) => ({
        id: c.id,
        name: c.name,
        participantIds: ["demo-admin", "demo-teacher", "demo-parent"],
      }));
    }
    // Teacher: only their classroom(s)
    return DEMO_CLASSROOMS.filter(
      (c) => c.id === "00000000-0000-0000-0000-000000000101",
    ).map((c) => ({
      id: c.id,
      name: c.name,
      participantIds: ["demo-teacher", "demo-parent", "demo-admin"],
    }));
  }, [role]);

  const onCompose = async (payload) => {
    try {
      const conv = await createConversation({
        ...payload,
        createdBy: profile.id,
        organizationId: profile.organization_id,
      });
      // If the composer included attachments, pin them to the first
      // message we just created. Mirrors the thread-reply attachment
      // path (uploadMessageAttachments needs a message id).
      const files = payload.attachments || [];
      if (files.length > 0 && conv?.first_message?.id) {
        try {
          await uploadMessageAttachments({
            conversationId: conv.id,
            messageId: conv.first_message.id,
            organizationId: profile.organization_id,
            uploaderId: profile.id,
            files,
          });
        } catch (err) {
          // Thread + first body landed; only attachments failed. Tell the
          // user clearly so they can retry from the thread itself.
          toast.error(
            `Message sent but ${files.length === 1 ? "the attachment" : "attachments"} failed: ${err.message}`,
          );
          setComposeOpen(false);
          await load();
          return;
        }
      }
      toast.success("Message sent");
      setComposeOpen(false);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not send message");
    }
  };

  return (
    <div data-testid="messaging-inbox" className="space-y-6">
      <section className="flex items-start justify-between gap-4">
        <div>
          <p className="overline text-primary-hover">Messages</p>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Inbox
          </h1>
        </div>
        {canCompose && (
          <button
            type="button"
            data-testid="messaging-compose-button"
            onClick={(event) => {
              composeTriggerRef.current = event.currentTarget;
              setComposeOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
          >
            <PencilSimpleLine size={16} weight="duotone" />
            Compose
          </button>
        )}
      </section>

      {loading ? (
        <div
          data-testid="messaging-inbox-loading"
          className="flex items-center justify-center py-16"
        >
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : conversations.length === 0 ? (
        <div
          data-testid="messaging-inbox-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500"
        >
          No conversations yet.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="messaging-inbox-list">
          {conversations.map((c) => (
            <li key={c.id}>
              <ConversationRow
                conversation={c}
                basePath={basePath}
                helpers={helpers}
              />
            </li>
          ))}
        </ul>
      )}

      {canCompose && (
        <ComposeDialog
          open={composeOpen}
          onOpenChange={setComposeOpen}
          role={role}
          families={composeFamilies}
          classrooms={composeClassrooms}
          onSubmit={onCompose}
          restoreFocusRef={composeTriggerRef}
        />
      )}
    </div>
  );
}
